import { useEffect, useState } from 'react'
import { InventoryItem, Venue, VenueEvent } from 'types/model'

import { Button } from '@elements/button'
import { Modal, ModalProps } from '@elements/modal'

import { EventHeader } from '@modules/event/views/event-header'
import useRequest from '@hooks/request'
import { utcToISO } from '@utils/date-time'
import { ErrorNote } from '@elements/error-note'
import { InventoryDetailDisplay } from './inventory-detail-display'

export interface InventoryDetailModalProps extends ModalProps {
  venue?: Venue
  event?: VenueEvent
  inventory?: InventoryItem
  onCheckout?: (vals: { inventoryId: string; quantity: number; eventId?: string }) => void
}

export function InventoryDetailModal({
  inventory,
  venue,
  event,
  onCheckout,
  isOpen,
  ...modalProps
}: InventoryDetailModalProps) {
  const [requestError, setRequestError] = useState<string>()
  const [quantity, setQuantity] = useState<number | null | undefined>(1)

  const [proceed, proceeding] = useRequest(async () => {
    if (inventory && !!quantity) {
      try {
        setRequestError(undefined)
      } catch (e) {
        if (e instanceof Error) setRequestError(e.message)
      }
      await onCheckout?.({ inventoryId: inventory.id, quantity, eventId: event?.id })
    }
  }, [inventory, onCheckout, quantity])

  useEffect(() => {
    if (!isOpen) {
      // clear values on close
      setTimeout(() => {
        setQuantity(1)
        setRequestError(undefined)
      }, 250)
    }
  }, [isOpen])

  return (
    <>
      <Modal
        {...modalProps}
        isOpen={isOpen}
        inline
        className="w-full h-full sm:h-auto sm:max-w-xl sm:mx-auto sm:max-h-[calc(100vh-48px)] sm:rounded"
        header={
          event &&
          venue && (
            <EventHeader
              venueName={venue.name}
              venueAddress={venue.location.address}
              eventName={event.name}
              description={event.description}
              eventDate={utcToISO(event.dateStart)}
              eventTimezone={event.timezone}
              eventPoster={event.primaryImage?.filename}
            />
          )
        }
        footer={
          inventory && (
            <div className="flex flex-col w-full space-y-3">
              <Button.Primary disabled={(quantity ?? 0) < 1} onClick={proceed} loading={proceeding}>
                Proceed to Checkout
              </Button.Primary>
              <ErrorNote>{requestError}</ErrorNote>
            </div>
          )
        }
      >
        <InventoryDetailDisplay
          venue={venue}
          inventory={inventory}
          quantity={quantity}
          setQuantity={setQuantity}
        />
      </Modal>
    </>
  )
}
